import { faker } from '@faker-js/faker';
import { useLayoutEffect, useRef, useState } from 'react';
import { type Control, useFormContext } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { AddFreight } from './AddFreight.tsx';
import { CustomerInput } from './CustomerInput.tsx';
import { LocationForm } from './LocationForm.tsx';
import { LocationNavbar } from './LocationNavbar.tsx';
import type { OrderDialogState } from './OrderDialog.tsx';
import type { OrderFormData, OrderFormPage } from './OrderFormValidation.ts';
import { OrderTitleInput } from './OrderTitleInput.tsx';
import { dateFieldToJSDate } from './timeWindowFields.ts';

type OrderFormProps = {
    control: Control<OrderFormData>;
    page: OrderFormPage;
    state: OrderDialogState;
    showLoadingStop: () => void;
    showUnloadingStop: () => void;
    showOrderMaterial: () => void;
};

export const OrderForm = ({
    control,
    page,
    state,
    showLoadingStop,
    showUnloadingStop,
    showOrderMaterial,
}: OrderFormProps) => {
    const intl = useIntl();
    const loadingStopRef = useRef<HTMLDivElement>(null);
    const hasMeasuredLoadingStopRef = useRef(false);
    const [locationFormMinHeight, setLocationFormMinHeight] = useState<number>();

    const {
        trigger,
        watch,
        setValue,
        formState: { isSubmitted },
    } = useFormContext<OrderFormData>();

    const loadingStop = watch('loadingStop');
    const loadingStartTargetMinDate = dateFieldToJSDate(loadingStop.startDate);

    const handleCreateRandomOrderId = async () => {
        setValue('number', String(faker.number.int()));
        await trigger('number');
    };

    useLayoutEffect(() => {
        if (page !== 'loadingStop' || hasMeasuredLoadingStopRef.current) {
            return;
        }

        const frameId = window.requestAnimationFrame(() => {
            const height = loadingStopRef.current?.offsetHeight ?? 0;

            if (height > 0) {
                setLocationFormMinHeight(height);
                hasMeasuredLoadingStopRef.current = true;
            }
        });

        return () => {
            window.cancelAnimationFrame(frameId);
        };
    }, [page]);

    return (
        <div>
            <div className='display-grid grid-cols-2-max-content-1fr-sm column-gap-20-sm padding-x-20 padding-top-20 align-items-end'>
                <OrderTitleInput intl={intl} control={control} onClick={handleCreateRandomOrderId} />
                <CustomerInput intl={intl} control={control} />
            </div>
            <div className='padding-x-20'>
                <LocationNavbar
                    page={page}
                    showLoadingStop={showLoadingStop}
                    showUnloadingStop={showUnloadingStop}
                    showOrderMaterial={showOrderMaterial}
                    isSubmitted={isSubmitted}
                    trigger={trigger}
                />
            </div>

            <div
                className='padding-x-20'
                style={locationFormMinHeight ? { minHeight: `${locationFormMinHeight}px` } : undefined}
            >
                {page === 'loadingStop' && (
                    <div ref={loadingStopRef} className='padding-y-20'>
                        <LocationForm addressType={page} />
                    </div>
                )}

                {page === 'unloadingStop' && (
                    <div className='padding-y-20'>
                        <LocationForm addressType={page} minDate={loadingStartTargetMinDate} />
                    </div>
                )}

                {page === 'orderMaterial' && <AddFreight state={state} />}
            </div>
        </div>
    );
};
